import { AddressValidationResult } from '../../../../../types/websheet';
import { CA_PROVINCES, USA_STATES } from '../constants';
import { AddressFormValues } from '../types';

/**
 * Google Address Validation → form mapping + verdict normalization
 * (UES-11808, "Suggested Address Found" screen — Figma 2454-871).
 *
 * A separate mapper from mapPlaceDetails: AV components carry
 * `componentType: string` + `componentName.text`, not the Place Details
 * `types: string[]` + `longText` shape. City/province/postal prefer
 * `postalAddress` — its `administrativeArea` is already the 2-letter code the
 * province/state dropdown uses; the `administrative_area_level_1` component
 * holds the long name and needs a lookup.
 */

export type VerdictKind = 'confirmed' | 'corrected' | 'unconfirmed';

export interface NormalizedValidation {
  kind: VerdictKind;
  suggested: AddressFormValues;
  formattedSuggested: string;
  changedFields: (keyof AddressFormValues)[];
}

const REGION_OPTIONS = [...USA_STATES, ...CA_PROVINCES];

const toRegionCode = (value: string): string => {
  const trimmed = value.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  const match = REGION_OPTIONS.find(option => {
    return option.label.toLowerCase() === trimmed.toLowerCase();
  });
  return match ? match.value : trimmed;
};

const componentText = (
  result: AddressValidationResult,
  componentType: string,
): string => {
  const found = result.address.addressComponents.find(component => {
    return component.componentType === componentType;
  });
  return found ? found.componentName.text : '';
};

export const mapValidationResult = (
  result: AddressValidationResult,
): AddressFormValues => {
  const postal = result.address.postalAddress;

  return {
    street_number: componentText(result, 'street_number'),
    address_line_1: componentText(result, 'route'),
    address_line_2: componentText(result, 'subpremise'),
    city: postal?.locality || componentText(result, 'locality'),
    state_province_region: toRegionCode(
      postal?.administrativeArea ||
        componentText(result, 'administrative_area_level_1'),
    ),
    zip_postal_code: postal?.postalCode || componentText(result, 'postal_code'),
  };
};

const normalizeForDiff = (field: keyof AddressFormValues, value: string) => {
  const collapsed = value.trim().toLowerCase();
  if (field === 'zip_postal_code') {
    return collapsed.replace(/\s+/g, '');
  }
  return collapsed;
};

/**
 * Components Google leaves out of the response (a `subpremise` it could not
 * resolve, a `route`-less premise, …) map to `''`. Accepting such a suggestion
 * wholesale would silently strip the user's apartment / civic number from the
 * E911 payload, so every empty suggested field falls back to what the user
 * typed. No re-validation is needed: each merged field is at least the user's
 * value, and those already passed the form schema at submit time.
 */
const mergeWithUserValues = (
  suggested: AddressFormValues,
  userValues: AddressFormValues,
): AddressFormValues => {
  return (Object.keys(suggested) as (keyof AddressFormValues)[]).reduce(
    (merged, field) => {
      return {
        ...merged,
        [field]: suggested[field].trim() ? suggested[field] : userValues[field],
      };
    },
    { ...suggested },
  );
};

export const normalizeValidation = (
  userValues: AddressFormValues,
  result: AddressValidationResult,
): NormalizedValidation => {
  const suggested = mergeWithUserValues(
    mapValidationResult(result),
    userValues,
  );

  const changedFields = (
    Object.keys(suggested) as (keyof AddressFormValues)[]
  ).filter(field => {
    return (
      normalizeForDiff(field, suggested[field]) !==
      normalizeForDiff(field, userValues[field])
    );
  });

  // The verdict flags (replaced / inferred / spellCorrected) are NOT a
  // reliable signal: Google routinely normalises a street ("Dery Road West"
  // → "Derry Road West", "Van Norden Street" → "Van Norden Road") and
  // reports the component as CONFIRMED with no flag at all. The only
  // trustworthy signal that Google suggests something different is the
  // merged result actually differing from what the subscriber typed.
  let kind: VerdictKind;
  if (changedFields.length > 0) {
    kind = 'corrected';
  } else if (result.verdict.hasUnconfirmedComponents) {
    kind = 'unconfirmed';
  } else {
    // Also lands here when verdict flags are set but nothing actually
    // differs — never show a suggestion identical to the input.
    kind = 'confirmed';
  }

  return {
    kind,
    suggested,
    formattedSuggested: result.address.formattedAddress,
    changedFields,
  };
};
